import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Vaccines as VaccineIcon,
  Biotech as BiotechIcon,
  MonitorHeart as MonitorHeartIcon,
  EventAvailable as EventAvailableIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Mail as MailIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityHighIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  HealthAndSafety as HealthAndSafetyIcon
} from '@mui/icons-material';
import {
  getCareGapSummary,
  getCareGapPopulationSummary,
  getPatientCareGaps,
  getCareGapOutreachList,
  getCareGapAnalytics,
  getCareGapRiskTiers,
  recordCareGapOutreach,
  closeCareGap,
  getCareGapCategoryColor,
  getCareGapCategoryIcon,
  getPriorityColor,
  getUrgencyColor,
  getUrgencyLabel,
  getCareGapRiskTierColor,
  getCareGapRiskTierLabel,
  getOutreachMethodIcon,
  getOutreachMethodLabel,
  formatDaysOverdue,
  getGapTypeDisplayName,
  getCategoryDisplayName
} from '../../services/careGapsService';
import { useUserData } from '../../hooks/useUserData';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  height: '100%',
}));

const StatCard = styled(Card)(({ theme, color }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  background: `linear-gradient(135deg, ${color || '#667eea'} 0%, ${adjustColor(color || '#667eea', -20)} 100%)`,
  color: 'white',
  height: '100%',
}));

const GapChip = styled(Chip)(({ gapcolor }) => ({
  backgroundColor: gapcolor,
  color: 'white',
  fontWeight: 600,
  fontSize: '0.75rem',
}));

// Helper function to adjust color brightness
function adjustColor(color, amount) {
  const clamp = (num) => Math.min(255, Math.max(0, num));
  const hex = color.replace('#', '');
  const r = clamp(parseInt(hex.substring(0, 2), 16) + amount);
  const g = clamp(parseInt(hex.substring(2, 4), 16) + amount);
  const b = clamp(parseInt(hex.substring(4, 6), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Get icon component for category
function getCategoryIconComponent(category) {
  switch (category) {
    case 'screening': return <BiotechIcon />;
    case 'immunization': return <VaccineIcon />;
    case 'chronic_disease': return <MonitorHeartIcon />;
    case 'preventive_visit': return <EventAvailableIcon />;
    default: return <AssessmentIcon />;
  }
}

// Sub-tabs for care gaps
const TABS = {
  OVERVIEW: 0,
  GAP_TYPES: 1,
  OUTREACH: 2,
  RISK_TIERS: 3
};

export default function CareGaps() {
  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
  const [error, setError] = useState(null);
  const { user } = useUserData();
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [populationSummary, setPopulationSummary] = useState([]);
  const [outreachList, setOutreachList] = useState([]);
  const [riskTiers, setRiskTiers] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // Per-tab loading states (for better UX)
  const [loadingTabs, setLoadingTabs] = useState({
    [TABS.OVERVIEW]: true,  // Start true since we load on mount
    [TABS.GAP_TYPES]: false,
    [TABS.OUTREACH]: false,
    [TABS.RISK_TIERS]: false
  });
  
  // Track which tabs have been loaded (for lazy loading)
  const [loadedTabs, setLoadedTabs] = useState({
    [TABS.OVERVIEW]: false,
    [TABS.GAP_TYPES]: false,
    [TABS.OUTREACH]: false,
    [TABS.RISK_TIERS]: false
  });
  
  // Dialog states
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientGaps, setPatientGaps] = useState(null);
  const [showPatientDialog, setShowPatientDialog] = useState(false);
  const [showOutreachDialog, setShowOutreachDialog] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [outreachForm, setOutreachForm] = useState({
    method: 'phone_call',
    outcome: 'contacted',
    notes: '',
    scheduledFollowUp: ''
  });
  
  // Pagination state
  const [outreachPage, setOutreachPage] = useState(0);
  const [outreachRowsPerPage, setOutreachRowsPerPage] = useState(10);

  const facilityId = user?.defaultFacility?.id;
  
  // Helper for global loading state
  const loading = Object.values(loadingTabs).some(v => v);

  // Fetch overview data (minimal data for overview tab - fast loading)
  const fetchOverviewData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.OVERVIEW]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.OVERVIEW]: true }));
      setError(null);
      
      // Only fetch summary for fast overview loading
      const summaryData = await getCareGapSummary(facilityId, 'production');
      setSummary(summaryData);
      
      setLoadedTabs(prev => ({ ...prev, [TABS.OVERVIEW]: true }));
    } catch (err) {
      console.error('Error fetching overview data:', err);
      setError('Failed to load overview data. Please try again.');
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.OVERVIEW]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch gap types tab data
  const fetchGapTypesData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.GAP_TYPES]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.GAP_TYPES]: true }));
      const [analyticsData, populationData] = await Promise.allSettled([
        getCareGapAnalytics(facilityId, 30, 'production'),
        getCareGapPopulationSummary(facilityId, 'production')
      ]);
      
      if (analyticsData.status === 'fulfilled') {
        setAnalytics(analyticsData.value);
      }
      if (populationData.status === 'fulfilled') {
        setPopulationSummary(populationData.value?.gapTypes || []);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.GAP_TYPES]: true }));
    } catch (err) {
      console.error('Error fetching gap types data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.GAP_TYPES]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch outreach tab data
  const fetchOutreachData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.OUTREACH]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.OUTREACH]: true }));
      const [outreachData] = await Promise.allSettled([
        getCareGapOutreachList(facilityId, 100, 'production')
      ]);
      
      if (outreachData.status === 'fulfilled') {
        setOutreachList(outreachData.value?.patients || []);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.OUTREACH]: true }));
    } catch (err) {
      console.error('Error fetching outreach data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.OUTREACH]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch risk tiers tab data
  const fetchRiskTiersData = useCallback(async () => {
    if (!facilityId || loadedTabs[TABS.RISK_TIERS]) return;
    
    try {
      setLoadingTabs(prev => ({ ...prev, [TABS.RISK_TIERS]: true }));
      const [riskTierData] = await Promise.allSettled([
        getCareGapRiskTiers(facilityId, 'production')
      ]);
      
      if (riskTierData.status === 'fulfilled') {
        setRiskTiers(riskTierData.value);
      }
      
      setLoadedTabs(prev => ({ ...prev, [TABS.RISK_TIERS]: true }));
    } catch (err) {
      console.error('Error fetching risk tiers data:', err);
    } finally {
      setLoadingTabs(prev => ({ ...prev, [TABS.RISK_TIERS]: false }));
    }
  }, [facilityId, loadedTabs]);

  // Fetch all data (for refresh button)
  const fetchData = useCallback(async () => {
    // Reset loaded tabs to force refresh
    setLoadedTabs({
      [TABS.OVERVIEW]: false,
      [TABS.GAP_TYPES]: false,
      [TABS.OUTREACH]: false,
      [TABS.RISK_TIERS]: false
    });
    
    // Fetch current tab data
    if (activeTab === TABS.OVERVIEW) {
      await fetchOverviewData();
    } else if (activeTab === TABS.GAP_TYPES) {
      await fetchGapTypesData();
    } else if (activeTab === TABS.OUTREACH) {
      await fetchOutreachData();
    } else if (activeTab === TABS.RISK_TIERS) {
      await fetchRiskTiersData();
    }
  }, [activeTab, fetchOverviewData, fetchGapTypesData, fetchOutreachData, fetchRiskTiersData]);

  // Load initial overview data
  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  // Lazy load tab data when tab changes
  useEffect(() => {
    if (activeTab === TABS.GAP_TYPES && !loadedTabs[TABS.GAP_TYPES]) {
      fetchGapTypesData();
    } else if (activeTab === TABS.OUTREACH && !loadedTabs[TABS.OUTREACH]) {
      fetchOutreachData();
    } else if (activeTab === TABS.RISK_TIERS && !loadedTabs[TABS.RISK_TIERS]) {
      fetchRiskTiersData();
    }
  }, [activeTab, loadedTabs, fetchGapTypesData, fetchOutreachData, fetchRiskTiersData]);

  // Handle patient care gaps view
  const handleViewPatientGaps = async (patientId) => {
    try {
      setLoadingPatient(true);
      setSelectedPatient(patientId);
      setShowPatientDialog(true);
      
      const gaps = await getPatientCareGaps(patientId, facilityId);
      setPatientGaps(gaps);
    } catch (err) {
      console.error('Error fetching patient care gaps:', err);
      setPatientGaps(null);
    } finally {
      setLoadingPatient(false);
    }
  };

  // Handle outreach submission
  const handleOutreachSubmit = async () => {
    if (!selectedPatient) return;
    
    try {
      await recordCareGapOutreach(selectedPatient, {
        facilityId,
        patientId: selectedPatient,
        ...outreachForm
      });
      setShowOutreachDialog(false);
      setOutreachForm({
        method: 'phone_call',
        outcome: 'contacted',
        notes: '',
        scheduledFollowUp: ''
      });
      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error recording outreach:', err);
    }
  };

  // Render overview tab
  const renderOverview = () => (
    <Box>
      {loadingTabs[TABS.OVERVIEW] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading care gaps data...</Typography>
        </Box>
      )}
      {/* Summary Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3, opacity: loadingTabs[TABS.OVERVIEW] ? 0.5 : 1 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#f44336">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Total Care Gaps
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {summary?.summary?.totalGaps || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {summary?.summary?.totalPatientsWithGaps || 0} patients affected
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#ff9800">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PriorityHighIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  High Priority
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {summary?.summary?.highPriorityGaps || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Urgent action needed
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#d32f2f">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Severely Overdue
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {summary?.summary?.severelyOverdueGaps || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Over 1 year overdue
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard color="#4caf50">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupsIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                  Patients Affected
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {summary?.summary?.totalPatientsWithGaps || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Need care gap closure
              </Typography>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>

      {/* Gap Distribution by Category */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {summary?.byCategory?.map((cat) => (
          <Grid item xs={12} sm={6} md={3} key={cat.category}>
            <ModernCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      p: 1, 
                      borderRadius: 2, 
                      backgroundColor: `${getCareGapCategoryColor(cat.category)}20`,
                      color: getCareGapCategoryColor(cat.category),
                      mr: 2 
                    }}
                  >
                    {getCategoryIconComponent(cat.category)}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {getCategoryDisplayName(cat.category)}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {cat.totalGaps}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${cat.highPriority} high`} 
                    size="small" 
                    sx={{ backgroundColor: '#ffebee', color: '#f44336' }}
                  />
                  <Chip 
                    label={`${cat.severelyOverdue} critical`} 
                    size="small" 
                    sx={{ backgroundColor: '#fce4ec', color: '#d32f2f' }}
                  />
                </Box>
              </CardContent>
            </ModernCard>
          </Grid>
        ))}
      </Grid>

      {/* Quick Link to Outreach */}
      <ModernCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ mr: 2, color: '#2196F3', fontSize: 32 }} />
              <Box>
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                  Patient Outreach Worklist
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  View prioritized patient list for care gap outreach
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={() => setActiveTab(TABS.OUTREACH)}
              sx={{ 
                backgroundColor: '#2196F3',
                '&:hover': { backgroundColor: '#1976D2' }
              }}
            >
              View Outreach List
            </Button>
          </Box>
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Render gap types tab
  const renderGapTypes = () => (
    <Box>
      {loadingTabs[TABS.GAP_TYPES] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading gap types data...</Typography>
        </Box>
      )}
      <ModernCard sx={{ opacity: loadingTabs[TABS.GAP_TYPES] ? 0.5 : 1 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Care Gap Population Analysis
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Gap Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell align="right">Patients</TableCell>
                  <TableCell align="right">Severely Overdue</TableCell>
                  <TableCell align="right">Overdue</TableCell>
                  <TableCell align="right">Avg Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {populationSummary.map((gap) => (
                  <TableRow key={gap.gapType} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box 
                          sx={{ 
                            p: 0.5, 
                            borderRadius: 1, 
                            backgroundColor: `${getCareGapCategoryColor(gap.category)}20`,
                            color: getCareGapCategoryColor(gap.category),
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {getCategoryIconComponent(gap.category)}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {gap.gapName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {gap.gapType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getCategoryDisplayName(gap.category)} 
                        size="small"
                        sx={{ 
                          backgroundColor: `${getCareGapCategoryColor(gap.category)}20`,
                          color: getCareGapCategoryColor(gap.category)
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <GapChip 
                        label={gap.priority}
                        gapcolor={getPriorityColor(gap.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {gap.patientCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        sx={{ color: gap.severelyOverdue > 0 ? '#d32f2f' : 'inherit' }}
                      >
                        {gap.severelyOverdue}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{gap.overdue}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="textSecondary">
                        {gap.avgDaysOverdue} days
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {populationSummary.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No care gap data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Render outreach tab
  const renderOutreach = () => (
    <Box>
      {loadingTabs[TABS.OUTREACH] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading outreach data...</Typography>
        </Box>
      )}
      <ModernCard sx={{ opacity: loadingTabs[TABS.OUTREACH] ? 0.5 : 1 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Patient Outreach Worklist
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {outreachList.length} patients in queue
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Total Gaps</TableCell>
                  <TableCell>High Priority</TableCell>
                  <TableCell>Engagement</TableCell>
                  <TableCell>Closure Prob</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {outreachList
                  .slice(outreachPage * outreachRowsPerPage, outreachPage * outreachRowsPerPage + outreachRowsPerPage)
                  .map((patient) => (
                  <TableRow key={patient.patientId} hover>
                    <TableCell>
                      <Chip 
                        label={`#${patient.rank}`}
                        size="small"
                        color={patient.rank <= 10 ? 'error' : patient.rank <= 25 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 500 }}
                        onClick={() => handleViewPatientGaps(patient.patientId)}
                      >
                        {patient.firstName} {patient.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Age: {patient.age}, {patient.gender}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {patient.phone && (
                          <Typography variant="caption" display="block">
                            {patient.phone}
                          </Typography>
                        )}
                        {patient.email && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            {patient.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {patient.totalGaps}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {patient.highPriorityGaps > 0 ? (
                        <GapChip 
                          label={patient.highPriorityGaps}
                          gapcolor={getPriorityColor('high')}
                          size="small"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">0</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={patient.engagementScore}
                          sx={{
                            width: 60,
                            mr: 1,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'rgba(0,0,0,0.08)',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: patient.engagementScore >= 60 ? '#4caf50' : 
                                patient.engagementScore >= 40 ? '#ff9800' : '#f44336',
                            }
                          }}
                        />
                        <Typography variant="caption">{patient.engagementScore}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500,
                          color: patient.closureProbability >= 0.6 ? '#4caf50' : 
                            patient.closureProbability >= 0.4 ? '#ff9800' : '#f44336'
                        }}
                      >
                        {Math.round(patient.closureProbability * 100)}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          patient.recommendedMethod === 'phone_call' ? <PhoneIcon fontSize="small" /> :
                          patient.recommendedMethod === 'patient_portal' ? <EmailIcon fontSize="small" /> :
                          <MailIcon fontSize="small" />
                        }
                        label={getOutreachMethodLabel(patient.recommendedMethod)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Gaps">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewPatientGaps(patient.patientId)}
                        >
                          <AssessmentIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Record Outreach">
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setSelectedPatient(patient.patientId);
                            setShowOutreachDialog(true);
                          }}
                        >
                          <PhoneIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {outreachList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No patients in outreach queue
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={outreachList.length}
            page={outreachPage}
            onPageChange={(e, newPage) => setOutreachPage(newPage)}
            rowsPerPage={outreachRowsPerPage}
            onRowsPerPageChange={(e) => {
              setOutreachRowsPerPage(parseInt(e.target.value, 10));
              setOutreachPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Render risk tiers tab
  const renderRiskTiers = () => (
    <Box>
      {loadingTabs[TABS.RISK_TIERS] && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, mb: 2 }}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }} color="textSecondary">Loading risk tiers data...</Typography>
        </Box>
      )}
      {/* Risk Tier Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3, opacity: loadingTabs[TABS.RISK_TIERS] ? 0.5 : 1 }}>
        {riskTiers?.riskTiers?.map((tier) => (
          <Grid item xs={12} sm={6} md={3} key={tier.tier}>
            <ModernCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: getCareGapRiskTierColor(tier.tier),
                      mr: 1 
                    }} 
                  />
                  <Typography variant="subtitle2" color="textSecondary">
                    {getCareGapRiskTierLabel(tier.tier)}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {tier.patientCount}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {tier.percentage}% of patients
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Avg Gaps</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{tier.avgGaps}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Engagement</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{tier.avgEngagement}%</Typography>
                  </Box>
                </Box>
              </CardContent>
            </ModernCard>
          </Grid>
        ))}
      </Grid>

      {/* Risk Distribution Visualization */}
      <ModernCard>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Patient Risk Distribution
          </Typography>
          {riskTiers?.riskTiers?.map((tier) => (
            <Box key={tier.tier} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" component="div" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                  <Box 
                    component="span"
                    sx={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: '50%', 
                      backgroundColor: getCareGapRiskTierColor(tier.tier),
                      mr: 1,
                      display: 'inline-block'
                    }} 
                  />
                  {getCareGapRiskTierLabel(tier.tier)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {tier.patientCount} patients ({tier.percentage}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={tier.percentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getCareGapRiskTierColor(tier.tier),
                    borderRadius: 5,
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                  Avg High Priority: {tier.avgHighPriority}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Avg Closure: {tier.avgClosureProbability}%
                </Typography>
              </Box>
            </Box>
          ))}
        </CardContent>
      </ModernCard>
    </Box>
  );

  // Patient Gaps Dialog
  const PatientGapsDialog = () => (
    <Dialog 
      open={showPatientDialog} 
      onClose={() => setShowPatientDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Patient Care Gaps
        {selectedPatient && (
          <Typography variant="subtitle2" color="textSecondary">
            ID: {selectedPatient}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loadingPatient ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : patientGaps ? (
          <Box>
            {/* Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#f44336' }}>
                    {patientGaps.summary?.totalGaps || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Total Gaps</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                    {patientGaps.summary?.highPriorityGaps || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">High Priority</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                    {patientGaps.summary?.severelyOverdueCount || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Severely Overdue</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {patientGaps.summary?.maxDaysOverdue || 0}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">Max Days Overdue</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Gap List */}
            <List>
              {patientGaps.careGaps?.map((gap, index) => (
                <React.Fragment key={gap.gapId}>
                  <ListItem alignItems="flex-start">
                    <Box 
                      sx={{ 
                        p: 1, 
                        borderRadius: 1, 
                        backgroundColor: `${getCareGapCategoryColor(gap.category)}20`,
                        color: getCareGapCategoryColor(gap.category),
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {getCategoryIconComponent(gap.category)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {gap.gapName}
                          </Typography>
                          <GapChip 
                            label={gap.priority}
                            gapcolor={getPriorityColor(gap.priority)}
                            size="small"
                          />
                          <Chip 
                            label={getUrgencyLabel(gap.urgencyLevel)}
                            size="small"
                            sx={{ 
                              backgroundColor: `${getUrgencyColor(gap.urgencyLevel)}20`,
                              color: getUrgencyColor(gap.urgencyLevel)
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            {gap.description}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Days Overdue:</strong> {formatDaysOverdue(gap.daysOverdue)}
                            {gap.lastServiceDate && (
                              <span> | <strong>Last Service:</strong> {new Date(gap.lastServiceDate).toLocaleDateString()}</span>
                            )}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < patientGaps.careGaps.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
              {(!patientGaps.careGaps || patientGaps.careGaps.length === 0) && (
                <ListItem>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon sx={{ color: '#4caf50', mr: 1 }} />
                        <Typography variant="body1" color="textSecondary">
                          No active care gaps
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Box>
        ) : (
          <Alert severity="warning">
            Unable to load patient care gaps
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowPatientDialog(false)}>Close</Button>
        <Button 
          variant="contained"
          onClick={() => {
            setShowPatientDialog(false);
            setShowOutreachDialog(true);
          }}
        >
          Record Outreach
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Outreach Dialog
  const OutreachDialog = () => (
    <Dialog 
      open={showOutreachDialog} 
      onClose={() => setShowOutreachDialog(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Record Outreach Attempt</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Contact Method</InputLabel>
            <Select
              value={outreachForm.method}
              label="Contact Method"
              onChange={(e) => setOutreachForm({ ...outreachForm, method: e.target.value })}
            >
              <MenuItem value="phone_call">Phone Call</MenuItem>
              <MenuItem value="patient_portal">Patient Portal</MenuItem>
              <MenuItem value="mail">Mail</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="in_person">In Person</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Outcome</InputLabel>
            <Select
              value={outreachForm.outcome}
              label="Outcome"
              onChange={(e) => setOutreachForm({ ...outreachForm, outcome: e.target.value })}
            >
              <MenuItem value="contacted">Contacted - Appointment Scheduled</MenuItem>
              <MenuItem value="voicemail">Left Voicemail</MenuItem>
              <MenuItem value="no_answer">No Answer</MenuItem>
              <MenuItem value="scheduled">Scheduled Follow-up</MenuItem>
              <MenuItem value="declined">Patient Declined</MenuItem>
              <MenuItem value="wrong_number">Wrong Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={outreachForm.notes}
            onChange={(e) => setOutreachForm({ ...outreachForm, notes: e.target.value })}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="date"
            label="Scheduled Follow-up"
            value={outreachForm.scheduledFollowUp}
            onChange={(e) => setOutreachForm({ ...outreachForm, scheduledFollowUp: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowOutreachDialog(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleOutreachSubmit}>
          Save Outreach
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Main render
  if (!facilityId) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Alert severity="warning">
          No facility access available. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      {/* Page Header */}
      <HeaderCard>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HealthAndSafetyIcon sx={{ fontSize: 32, color: 'white' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                  Care Gaps
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Identify and close preventive care gaps with AI-powered insights
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh Data">
              <span>
                <IconButton onClick={fetchData} disabled={loading} sx={{ color: 'white' }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </HeaderCard>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Sub-tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 500,
            }
          }}
        >
          <Tab 
            icon={<AssessmentIcon />} 
            label="Overview" 
            iconPosition="start"
          />
          <Tab 
            icon={<BiotechIcon />} 
            label="Gap Types" 
            iconPosition="start"
          />
          <Tab 
            icon={<PhoneIcon />} 
            label="Outreach" 
            iconPosition="start"
          />
          <Tab 
            icon={<TrendingUpIcon />} 
            label="Risk Tiers" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeTab === TABS.OVERVIEW && renderOverview()}
        {activeTab === TABS.GAP_TYPES && renderGapTypes()}
        {activeTab === TABS.OUTREACH && renderOutreach()}
        {activeTab === TABS.RISK_TIERS && renderRiskTiers()}
      </Box>

      {/* Dialogs */}
      <PatientGapsDialog />
      <OutreachDialog />
    </Box>
  );
}
